import { useState, useEffect, useCallback } from 'react';
import type { SaRole, PermissionAction, PermissionEngine } from '@/mocks/saRolesPermissions';
import { ALL_ENGINES, ALL_ACTIONS } from '@/mocks/saRolesPermissions';
import { ENGINE_LABELS, ENGINE_ICONS } from './roleLabels';

interface FormData {
  name: string;
  slug: string;
  description: string;
  icon: string;
  color: string;
  hierarchyLevel: number;
  active: boolean;
}

interface Props {
  mode: 'create' | 'edit' | 'duplicate';
  role: SaRole | null;
  onSave: (data: FormData) => void;
  onClose: () => void;
}

const ICON_OPTIONS = [
  'ri-shield-star-line', 'ri-settings-4-line', 'ri-mental-health-line',
  'ri-bank-card-line', 'ri-shield-check-line', 'ri-radio-line',
  'ri-user-settings-line', 'ri-team-line', 'ri-user-voice-line',
  'ri-user-line', 'ri-advertisement-line', 'ri-vip-crown-line',
  'ri-scales-3-line', 'ri-admin-line', 'ri-key-2-line',
];

const COLOR_OPTIONS = [
  '#0f172a', '#1e40af', '#0d9488', '#ca8a04', '#7c3aed',
  '#dc2626', '#ea580c', '#0891b2', '#a21caf', '#059669',
  '#b45309', '#e11d48', '#4f46e5', '#334155', '#15803d',
];

export default function RoleFormModal({ mode, role, onSave, onClose }: Props) {
  const [visible, setVisible] = useState(false);

  const getInitialData = useCallback((): FormData => {
    if (mode === 'create') {
      return {
        name: '',
        slug: '',
        description: '',
        icon: 'ri-admin-line',
        color: '#4f46e5',
        hierarchyLevel: 8,
        active: true,
      };
    }
    if (role) {
      const prefix = mode === 'duplicate' ? '(Copia) ' : '';
      return {
        name: prefix + role.name,
        slug: (prefix ? role.slug + '-copy' : role.slug),
        description: role.description,
        icon: role.icon,
        color: role.color,
        hierarchyLevel: role.hierarchyLevel,
        active: role.active,
      };
    }
    return {
      name: '', slug: '', description: '', icon: 'ri-admin-line', color: '#4f46e5', hierarchyLevel: 8, active: true,
    };
  }, [mode, role]);

  const [form, setForm] = useState<FormData>(getInitialData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setForm(getInitialData());
    setErrors({});
  }, [mode, role, getInitialData]);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => onClose(), 200);
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'El nombre es requerido';
    if (!form.slug.trim()) e.slug = 'El slug es requerido';
    if (!form.description.trim()) e.description = 'La descripción es requerida';
    if (form.hierarchyLevel < 1 || form.hierarchyLevel > 10) e.hierarchyLevel = 'Nivel inválido (1-10)';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSave(form);
    handleClose();
  };

  const modeLabel = mode === 'create' ? 'Crear Rol' : mode === 'duplicate' ? 'Duplicar Rol' : 'Editar Rol';
  const modeIcon = mode === 'create' ? 'ri-add-circle-line' : mode === 'duplicate' ? 'ri-file-copy-line' : 'ri-edit-line';

  return (
    <div className={`fixed inset-0 z-[110] flex items-center justify-center p-4 transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-0'}`}>
      <div className="absolute inset-0 bg-black/40" onClick={handleClose}></div>

      <div className={`relative bg-background-50 rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl transition-transform duration-200 ${visible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'}`}>
        {/* Header */}
        <div className="px-6 py-5 border-b border-background-200 shrink-0 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center">
              <i className={`${modeIcon} text-foreground-600 text-lg`}></i>
            </div>
            <div>
              <h3 className="text-foreground-950 font-bold text-base">{modeLabel}</h3>
              <p className="text-foreground-400 text-[10px]">
                {mode === 'create' ? 'Definí el nuevo rol y sus propiedades básicas' : 'Modificá las propiedades del rol'}
              </p>
            </div>
          </div>
          <button onClick={handleClose} className="w-9 h-9 rounded-xl bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors">
            <i className="ri-close-line text-foreground-600 text-sm"></i>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-foreground-500 text-[10px] font-semibold uppercase tracking-wider mb-1.5">Nombre del Rol</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Ej: Super Admin, Listener VIP..."
              className={`w-full px-3 py-2.5 rounded-xl bg-background-50 border text-foreground-800 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-100 transition-all ${errors.name ? 'border-rose-300' : 'border-background-200'}`}
            />
            {errors.name && <p className="text-rose-500 text-[10px] mt-1">{errors.name}</p>}
          </div>

          {/* Slug */}
          <div>
            <label className="block text-foreground-500 text-[10px] font-semibold uppercase tracking-wider mb-1.5">Slug</label>
            <input
              type="text"
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') }))}
              placeholder="super-admin"
              className={`w-full px-3 py-2.5 rounded-xl bg-background-50 border text-foreground-800 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-100 transition-all font-mono ${errors.slug ? 'border-rose-300' : 'border-background-200'}`}
            />
            {errors.slug && <p className="text-rose-500 text-[10px] mt-1">{errors.slug}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="block text-foreground-500 text-[10px] font-semibold uppercase tracking-wider mb-1.5">Descripción</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Describí el propósito y alcance de este rol..."
              rows={3}
              className={`w-full px-3 py-2.5 rounded-xl bg-background-50 border text-foreground-800 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-100 transition-all resize-none ${errors.description ? 'border-rose-300' : 'border-background-200'}`}
            />
            {errors.description && <p className="text-rose-500 text-[10px] mt-1">{errors.description}</p>}
          </div>

          {/* Icon + Color + Hierarchy row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Icon */}
            <div>
              <label className="block text-foreground-500 text-[10px] font-semibold uppercase tracking-wider mb-1.5">Ícono</label>
              <div className="grid grid-cols-5 gap-1 mb-2">
                {ICON_OPTIONS.map((icon) => (
                  <button
                    key={icon}
                    onClick={() => setForm((f) => ({ ...f, icon }))}
                    className={`w-9 h-9 rounded-lg flex items-center justify-center cursor-pointer transition-all ${form.icon === icon ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500 hover:bg-background-200'}`}
                  >
                    <i className={`${icon} text-xs`}></i>
                  </button>
                ))}
              </div>
            </div>

            {/* Color */}
            <div>
              <label className="block text-foreground-500 text-[10px] font-semibold uppercase tracking-wider mb-1.5">Color</label>
              <div className="flex flex-wrap gap-1.5">
                {COLOR_OPTIONS.map((color) => (
                  <button
                    key={color}
                    onClick={() => setForm((f) => ({ ...f, color }))}
                    className={`w-7 h-7 rounded-lg cursor-pointer transition-all border-2 ${form.color === color ? 'border-foreground-950 scale-110' : 'border-transparent hover:scale-105'}`}
                    style={{ backgroundColor: color }}
                  ></button>
                ))}
              </div>
              <input
                type="text"
                value={form.color}
                onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                className="w-full mt-2 px-3 py-1.5 rounded-lg bg-background-50 border border-background-200 text-foreground-600 text-xs font-mono focus:outline-none"
              />
            </div>

            {/* Hierarchy */}
            <div>
              <label className="block text-foreground-500 text-[10px] font-semibold uppercase tracking-wider mb-1.5">Nivel Jerárquico (1-10)</label>
              <input
                type="number"
                min={1}
                max={10}
                value={form.hierarchyLevel}
                onChange={(e) => setForm((f) => ({ ...f, hierarchyLevel: Math.max(1, Math.min(10, parseInt(e.target.value) || 1)) }))}
                className={`w-full px-3 py-2.5 rounded-xl bg-background-50 border text-foreground-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100 transition-all ${errors.hierarchyLevel ? 'border-rose-300' : 'border-background-200'}`}
              />
              {errors.hierarchyLevel && <p className="text-rose-500 text-[10px] mt-1">{errors.hierarchyLevel}</p>}
              <div className="flex items-center gap-2 mt-2">
                <span className="text-foreground-400 text-[10px]">{form.hierarchyLevel === 1 ? 'Máxima autoridad' : form.hierarchyLevel <= 3 ? 'Administración' : form.hierarchyLevel <= 6 ? 'Gestión' : 'Usuario'}</span>
              </div>
            </div>
          </div>

          {/* Active toggle */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-background-100/70 border border-background-200">
            <div>
              <span className="text-foreground-700 text-sm font-semibold block">Rol Activo</span>
              <span className="text-foreground-400 text-[10px]">Los roles inactivos no otorgan permisos a sus usuarios.</span>
            </div>
            <button
              onClick={() => setForm((f) => ({ ...f, active: !f.active }))}
              className={`w-11 h-6 rounded-full transition-colors cursor-pointer ${form.active ? 'bg-emerald-500' : 'bg-background-300'}`}
            >
              <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${form.active ? 'translate-x-[22px]' : 'translate-x-0.5'}`}></div>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-background-200 shrink-0 flex items-center justify-end gap-2">
          <button
            onClick={handleClose}
            className="px-4 py-2.5 rounded-xl bg-background-100 text-foreground-600 text-xs font-semibold cursor-pointer hover:bg-background-200 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            className="px-5 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold cursor-pointer hover:bg-foreground-800 transition-colors whitespace-nowrap"
          >
            {mode === 'create' ? 'Crear Rol' : 'Guardar Cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}